import {
	FormDrawer,
	FormDrawerContent,
	FormDrawerContentProps,
	Id,
	SingleSelectField,
	TextField,
	apiIsOK,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { TransactionType as TransactionTypes } from "apiGen";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { employeeKeys } from "~shared/api/employee/employeeKeys";
import { webserviceApi } from "~shared/api/webserviceApi";
import { ITransactionFormState } from "~shared/types/transaction";

const emptyTransaction: ITransactionFormState = {
	value: 0,
	type: "specialvacation",
	calculation: "add",
};

interface Props {
	accountId: Id;
	employeeId: Id;
	open: boolean;
	handleClose: () => void;
}

const useValidate = () => {
	const { t } = useTranslation();

	return (values: ITransactionFormState) => {
		const errors: { [K in keyof ITransactionFormState]?: any } = {};

		if (!values.calculation) {
			errors.calculation = t("Field is required");
		}
		if (!values.value || values.value < 0) {
			errors.value = t("Value must be positive");
		}
		if (!values.type) {
			errors.type = t("Field is required");
		}

		return errors;
	};
};

const AccountTransactionDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();

	return (
		<FormDrawerContent {...props}>
			<SingleSelectField options={["add", "subtract"]} label={t("Calculation")} name="calculation" getLabel={t} />
			<TextField name="value" type="number" label={t("Value")} />
			<SingleSelectField
				options={Object.keys(TransactionTypes) as string[]}
				label={t("Transaction type")}
				name="type"
				getLabel={(x) => x}
			/>
		</FormDrawerContent>
	);
};

export const AccountTransactionDrawer: FC<Props> = ({ open, handleClose, accountId, employeeId }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { showToast } = useToasts();
	const validate = useValidate();

	const onSubmit = useCallback(
		async (formState: ITransactionFormState) => {
			const res =
				formState.calculation === "add"
					? await webserviceApi.addAccountTransaction(+accountId, formState)
					: await webserviceApi.subtractAccountTransaction(+accountId, formState);

			if (apiIsOK(res)) {
				showToast({
					type: "success",
					title: t("Transactions"),
					body: t("Transaction was saved."),
				});
				await queryClient.invalidateQueries({
					queryKey: employeeKeys.detail(employeeId),
				});
				return true;
			}
			return false;
		},
		[accountId, employeeId, queryClient, showToast, t],
	);

	return (
		<FormDrawer
			title={t("Create transaction")}
			open={open}
			initialValues={emptyTransaction}
			onSubmit={onSubmit}
			onClose={handleClose}
			onClosed={() => undefined}
			validate={validate}
			renderForm={(props) => <AccountTransactionDrawerContent {...props} />}
		/>
	);
};
